import {
  DRINKING_OPTIONS,
  EDUCATION_LEVELS,
  FOOD_PREFERENCES,
  GENDERS,
  MARITAL_STATUSES,
  SMOKING_OPTIONS,
  WANTS_CHILDREN_OPTIONS,
} from "../../../constants";
import type { Language, Profile } from "../../../types";
import { countryUsesState } from "../locationUtils";

export const RELOCATION_OPTIONS = [
  { value: "same_city", label: "I'd stay in my city" },
  { value: "same_country", label: "I'd move anywhere in my country" },
  { value: "anywhere", label: "I'd move anywhere in the world" },
] as const;

export const initialProfile: Partial<Profile> = {
  firstName: "",
  dateOfBirth: "",
  gender: "female",
  height: 170,
  city: "",
  state: "",
  country: "",
  religion: "bahai",
  maritalHistory: "never_married",
  hasChildren: false,
  numberOfChildren: 0,
  wantsChildren: "not_sure",
  smoking: "never",
  drinking: "never",
  foodPreference: "all_foods",
  occupation: "",
  educationLevel: "bachelors",
  languages: [{ language: "English", level: "fluent" }],
  interests: [],
  aboutMe: "",
  lookingFor: "",
  relocationWillingness: "same_country",
};

function enumValue<T extends string>(value: unknown, options: readonly { value: T }[], fallback: T): T {
  return options.some((option) => option.value === value) ? value as T : fallback;
}

function textValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function dateInputValue(value: unknown, fallback = initialProfile.dateOfBirth as string) {
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  return fallback;
}

function boundedInteger(value: unknown, fallback: number, min: number, max: number) {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.round(parsed)));
}

function normalizeLanguageLevel(level: Language["level"]): Language["level"] {
  if (level === "fluent") return "fluent";
  if (level === "conversational" || level === "spoken_written" || level === "spoken_only") {
    return "conversational";
  }
  return "basic";
}

function normalizeReligion(value: unknown): string {
  const religion = textValue(value, "bahai").trim();
  if (religion === "bahai_childhood" || religion === "bahai_adult") {
    return "bahai";
  }
  if (religion === "christian_background") {
    return "interested:Christianity";
  }
  if (religion === "muslim_background") {
    return "interested:Islam";
  }
  return religion;
}

export function normalizeProfilePayload(profile?: Partial<Profile>): Partial<Profile> {
  const source = { ...initialProfile, ...profile };
  const sourceLanguages = profile && Object.hasOwn(profile, "languages")
    ? profile.languages ?? []
    : initialProfile.languages ?? [];
  const languages = sourceLanguages
    .filter((language) => language.language && language.level)
    .map((language) => ({
      language: language.language.normalize("NFKC").trim().replace(/\s+/g, " "),
      level: normalizeLanguageLevel(language.level),
    }));
  const interests = (source.interests || []).filter((interest) => typeof interest === "string" && interest.trim()).map((interest) => interest.trim());
  const childCount = source.numberOfChildren == null ? undefined : boundedInteger(source.numberOfChildren, 0, 0, 20);
  const country = textValue(source.country).trim();

  return {
    firstName: textValue(source.firstName).trim(),
    dateOfBirth: dateInputValue(source.dateOfBirth),
    gender: enumValue(source.gender, GENDERS, "female"),
    height: boundedInteger(source.height, 170, 100, 250),
    city: textValue(source.city).trim(),
    state: countryUsesState(country) ? textValue(source.state).trim() : "",
    country,
    religion: normalizeReligion(source.religion),
    maritalHistory: enumValue(source.maritalHistory, MARITAL_STATUSES, "never_married"),
    hasChildren: !!source.hasChildren,
    numberOfChildren: childCount,
    wantsChildren: enumValue(source.wantsChildren, WANTS_CHILDREN_OPTIONS, "not_sure"),
    smoking: enumValue(source.smoking, SMOKING_OPTIONS, "never"),
    drinking: enumValue(source.drinking, DRINKING_OPTIONS, "never"),
    foodPreference: enumValue(source.foodPreference, FOOD_PREFERENCES, "all_foods"),
    occupation: textValue(source.occupation).trim(),
    educationLevel: enumValue(source.educationLevel, EDUCATION_LEVELS, "bachelors"),
    languages,
    interests,
    aboutMe: textValue(source.aboutMe),
    lookingFor: textValue(source.lookingFor),
    relocationWillingness: enumValue(source.relocationWillingness, RELOCATION_OPTIONS, "same_country"),
  };
}

export function profilePayloadEqual(a: Partial<Profile>, b: Partial<Profile>) {
  return JSON.stringify(normalizeProfilePayload(a)) === JSON.stringify(normalizeProfilePayload(b));
}

const FORM_SCROLL_ROOT_SELECTOR = ".onboarding-form-main, .preferences-form-scroll, .profile-form-scroll";

function isScrollableContainer(element: HTMLElement) {
  const style = getComputedStyle(element);
  const overflowY = style.overflowY;
  const allowsScroll = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
  return allowsScroll && element.scrollHeight > element.clientHeight + 1;
}

function findFormScrollRoot(field: HTMLElement) {
  let node: HTMLElement | null = field.parentElement;
  while (node) {
    if (isScrollableContainer(node)) {
      return node;
    }
    if (node.matches(".app-screen-content")) {
      break;
    }
    node = node.parentElement;
  }

  for (const selector of FORM_SCROLL_ROOT_SELECTOR.split(", ")) {
    const candidate = field.closest(selector.trim()) as HTMLElement | null;
    if (candidate && isScrollableContainer(candidate)) {
      return candidate;
    }
  }

  return (field.closest(".app-screen-content") as HTMLElement | null) ?? null;
}

function formScrollOffset(scrollRoot: HTMLElement) {
  if (scrollRoot.matches(".onboarding-form-main")) {
    return 12;
  }

  const sticky = scrollRoot.querySelector(".onboarding-progress-sticky-shell");
  if (sticky instanceof HTMLElement) {
    return sticky.offsetHeight + 10;
  }
  return 16;
}

export function scrollFormFieldIntoView(field: HTMLElement, behavior: ScrollBehavior = "smooth") {
  const scrollRoot = findFormScrollRoot(field);

  if (scrollRoot) {
    const top =
      field.getBoundingClientRect().top -
      scrollRoot.getBoundingClientRect().top +
      scrollRoot.scrollTop -
      formScrollOffset(scrollRoot);
    scrollRoot.scrollTo({ top: Math.max(0, top), behavior });
    return;
  }

  field.scrollIntoView({ behavior, block: "center" });
}

export function scrollValidationTargetIntoView(field: HTMLElement) {
  const target =
    (field.closest(".field") as HTMLElement | null) ??
    (field.closest("[data-profile-section]") as HTMLElement | null) ??
    field;
  scrollFormFieldIntoView(target);
}

export function scrollProfileSectionIntoView(form: HTMLElement, sectionKey: string) {
  const target = form.querySelector(`[data-profile-section="${sectionKey}"]`);
  if (target instanceof HTMLElement) {
    scrollFormFieldIntoView(target);
  }
}

export function focusFormField(field: HTMLElement) {
  try {
    field.focus({ preventScroll: true });
  } catch {
    field.focus();
  }
}

type ValidatedFormControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function isValidatedFormControl(field: unknown): field is ValidatedFormControl {
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement;
}

export function getFormControlFieldKey(control: ValidatedFormControl) {
  return control.dataset.profileField ?? control.name ?? control.id ?? "";
}

export function formatFormControlValidationError(control: ValidatedFormControl) {
  const label = control
    .closest(".field, .profile-language-control")
    ?.querySelector(":scope > span")
    ?.textContent
    ?.trim();

  if (control.validity.valueMissing) {
    return label ? `${label} is required.` : "This field is required.";
  }

  if (control.validity.tooShort) {
    const minLength = control instanceof HTMLSelectElement ? 0 : control.minLength;
    if (minLength > 0) {
      const remaining = Math.max(0, minLength - control.value.length);
      if (label) {
        return `${label} needs at least ${minLength} characters${remaining > 0 ? ` (${remaining} more to go)` : ""}.`;
      }
      return `Enter at least ${minLength} characters.`;
    }
  }

  const message = control.validationMessage.trim();
  if (!message) return label ? `Check ${label}.` : "Please check this field.";
  return label ? `${label}: ${message}` : message;
}

export function applyFormControlValidation(
  control: ValidatedFormControl,
  setFieldValidation: (value: { fieldKey: string; message: string } | null) => void,
) {
  const message = formatFormControlValidationError(control);
  const fieldKey = getFormControlFieldKey(control);
  setFieldValidation({ fieldKey, message });
  focusFormField(control);
}
